/**
 * WHERE EVERY SNIPPET LOADS FROM - one function, used by every surface that
 * hands out a tag (install code, the store pixel, the WhatsApp widget, chat,
 * popups, recommendations, the install matrix).
 *
 * Kept free of UI imports so it can be tested on its own.
 */
/**
 * Where the tenant's scripts should be loaded from, best transport first.
 *
 * This has to be one function. px.js and the Shopify pixel bake their POST
 * endpoint from wherever they were FETCHED, so the URL in the snippet is not
 * cosmetic - it decides whether every event afterwards is first-party or a
 * blockable cross-site request. A snippet still pointing at our domain keeps
 * sending there no matter what else is configured.
 *
 *   path       theirsite.com/<token> - nothing in DNS to detect. Best.
 *   subdomain  track.theirsite.com   - a visible CNAME, so Safari caps the
 *                                      cookie and blocker lists carry it.
 *   ours       api.cookiebees.io     - a third-party request. Works until it
 *                                      does not.
 */
export function scriptOrigin(td) {
  // WORKING, not configured and not "seen once, ever": the server's canon
  // (api services/firstPartyState) marks each transport working when it
  // carried a proven request in the last 24 hours, or when the whole site
  // was quiet. A transport that stopped makes every snippet fall back to our
  // domain until it works again, rather than hand out a tag for a proxy that
  // is not there.
  const fpState = td?.first_party || null;
  const why = { state: fpState?.state || (td ? 'none' : null), stopped: fpState?.stopped || [] };
  const pathRow = td?.path_mode;
  if (pathRow?.working && pathRow.site_host && pathRow.token) {
    return { kind: 'path', base: `https://${pathRow.site_host}/${pathRow.token}`, label: `${pathRow.site_host}/${pathRow.token}`, why };
  }
  const host = (td?.domains || []).find((d) => d.working)?.hostname || null;
  if (host) return { kind: 'subdomain', base: `https://${host}`, label: host, why };
  return { kind: 'ours', base: null, label: null, why };
}

/**
 * The origin for a surface only a tracking PATH can carry (the chat widget and
 * popups: a tracking subdomain is proxied by our nginx, which does not serve
 * them). Same answer as scriptOrigin, except a subdomain falls back to ours -
 * and says why, so the note under the snippet is still true.
 */
export function pathOnlyOrigin(fp) {
  if (fp?.kind === 'path') return fp;
  if (fp?.kind === 'subdomain') return { kind: 'ours', base: null, label: null, why: { ...(fp.why || {}), subdomainOnly: true } };
  return fp || { kind: 'ours', base: null, label: null, why: null };
}

// Where the tracking domain is set up. Every "temporary" note links here.
export const TRACKING_DOMAIN_SETUP = '/settings?tab=tracking&section=serving';

